import type { CreatePlanFormValues } from '../schemas/plan-schema';
import { IVA_RATE, withIva, formatMxn } from '../utils/pricing';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';

interface PlanPricePreviewDialogProps {
  /** El plan por confirmar, o nulo cuando no hay confirmación pendiente. */
  plan: CreatePlanFormValues | null;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Último paso antes de crear un plan: enseña el importe que el cliente va a ver.
 *
 * El formulario captura sin IVA y el catálogo del SaaS muestra con IVA, así que un plan de 500
 * se publica en 580. Sin esta pantalla el administrador lo descubría después de publicarlo.
 */
export function PlanPricePreviewDialog({
  plan,
  isPending,
  onCancel,
  onConfirm,
}: PlanPricePreviewDialogProps) {
  if (!plan) return null;

  const ivaPercent = Math.round(IVA_RATE * 100);

  return (
    <ConfirmDialog
      open
      onOpenChange={(v) => !v && onCancel()}
      title="Confirmar precios del plan"
      description={
        <span data-testid="plan-price-preview-text">
          El catálogo muestra los precios con IVA incluido. Así es como verá «{plan.name}» una
          cuenta:
        </span>
      }
      confirmLabel="Crear plan"
      cancelLabel="Volver a editar"
      confirmActionType="submit"
      onConfirm={onConfirm}
      isPending={isPending}
      data-testid="plan-price-preview-dialog"
    >
      <div className="border-outline flex flex-col gap-2 rounded-lg border p-3">
        <PriceRow
          label="Mensual"
          base={plan.priceMonthly}
          testId="plan-price-preview-monthly"
          ivaPercent={ivaPercent}
        />
        <PriceRow
          label="Anual"
          base={plan.priceAnnual}
          testId="plan-price-preview-annual"
          ivaPercent={ivaPercent}
        />
      </div>
    </ConfirmDialog>
  );
}

function PriceRow({
  label,
  base,
  testId,
  ivaPercent,
}: {
  label: string;
  base: number;
  testId: string;
  ivaPercent: number;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-primary text-xs">{label}</span>
      <span data-testid={testId} className="text-light text-sm">
        <span className="text-primary">{formatMxn(base)}</span>
        <span className="text-primary mx-1.5 text-xs">+ {ivaPercent}% IVA =</span>
        <span className="font-semibold">{formatMxn(withIva(base))}</span>
      </span>
    </div>
  );
}
